import React, { useCallback } from 'react';
import { makeStyles } from '@material-ui/styles';
import { type PartnerAd, PartnerAdPlacement, type PartnerAdVariant } from '@skyslope/partnerships-sdk';
import {
  PartnerAdInternal,
  PartnershipAdCloseButton,
  PartnershipsErrorBoundary,
  useAd,
  type PartnershipsFileMetadata,
  useOnDismissed,
} from '@skyslope/partnerships-sdk/react';
import { useSelector } from 'react-redux';
import { tokenInfo } from '../lib/token';
import type { IRootState } from '../store';

const useStyles = makeStyles({
  dsAdBox: {
    position: 'relative',
  },
});

export interface IDSAdProps {
  adVariant: PartnerAdVariant;
  id: string;
  imgProps?: React.ImgHTMLAttributes<HTMLImageElement>;
}

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const useEnvelopeData = () => {
  return useSelector((state: IRootState) => state.envelope.envelope);
};

export function DSAd(props: IDSAdProps) {
  const classes = useStyles();
  const envelope = useEnvelopeData();
  const formTypes = envelope?.documents
    .filter((document) => document.metadata?.formType != null)
    .map((document) => document.metadata.formType);

  const filterFn = useCallback((partnerAd: PartnerAd): boolean => {
    let showOnlyForFormTypes = partnerAd.metadata?.showOnlyForFormTypes;
    if (typeof showOnlyForFormTypes === 'string') {
      showOnlyForFormTypes = [showOnlyForFormTypes];
    }
    return (showOnlyForFormTypes as string[])?.some((formType) => formTypes?.includes(formType)) ?? false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userMetadata: PartnershipsFileMetadata = {
    clientDetails: [
      {
        name: [envelope?.signer?.firstName, envelope?.signer?.middleName, envelope?.signer?.lastName]
          .filter(Boolean)
          .join(' '),
        email: `${tokenInfo?.Email ?? ''}`,
      },
    ],
    subscriberId: `${envelope?.metadata?.ownerSubscriberId}`,
    fileAddress: {
      city: envelope?.metadata?.fileCity,
      zip: envelope?.metadata?.fileZip,
    },
  };

  const { ad } = useAd({
    variant: props.adVariant,
    placement: PartnerAdPlacement.DS3Signer,
    filterFn,
    metadata: userMetadata,
  });
  const { dismissed, onDismissed } = useOnDismissed(props.id);

  if (ad == null || dismissed) {
    return null;
  }

  return (
    <PartnershipsErrorBoundary>
      <div className={classes.dsAdBox}>
        <PartnerAdInternal
          ad={ad}
          id={props.id}
          variant={props.adVariant}
          imgProps={props.imgProps}
          metadata={userMetadata}
          placement={PartnerAdPlacement.DS3Signer}
        />
        <PartnershipAdCloseButton onDismissed={onDismissed} />
      </div>
    </PartnershipsErrorBoundary>
  );
}

// eslint-disable-next-line import/no-default-export -- This code was written prior to enforcing lint rules
export default DSAd;
